import { z } from 'zod/v3';

import { BadDataError } from '@watershed/errors/BadDataError';

const eventBaseSchema = z.object({
  calculationCustomizationId: z.string(),
});
const eventSchema = z.discriminatedUnion('eventKind', [
  eventBaseSchema.extend({
    eventKind: z.literal('createdCustomization'),
  }),
  eventBaseSchema.extend({
    eventKind: z.literal('publishedCustomization'),
  }),
  eventBaseSchema.extend({
    eventKind: z.literal('archivedCustomization'),
  }),
  eventBaseSchema.extend({
    eventKind: z.literal('updatedCustomizationMetadata'),
  }),
  eventBaseSchema.extend({
    eventKind: z.literal('updatedCustomizationContent'),
  }),
  eventBaseSchema.extend({
    eventKind: z.literal('approvedCustomizationForSchema'),
  }),
]);

export namespace lifecycleUtils {
  /**
   * Represents a lifecycle event related to a calculation customization.
   */
  export type Event = z.infer<typeof eventSchema>;
  /**
   * The specific type of a lifecycle event.
   */
  export type EventKind = Event['eventKind'];
  /**
   * Extracts a specific type of event based on its `eventKind`.
   *
   * @example
   * ```ts
   * type CreationEvent = lifecycleUtils.EventOfKind<'createdCustomization'>;
   * ```
   */
  export type EventOfKind<T extends EventKind> = Extract<
    Event,
    { eventKind: T }
  >;

  /**
   * Parses unknown input into a validated `Event` object.
   */
  export function parseEvent(
    event: unknown
  ): z.SafeParseReturnType<unknown, Event> {
    return eventSchema.safeParse(event);
  }

  /**
   * Utility type that adds parsed `eventKind` and `eventDetails` fields based
   * on the `Event` type.
   */
  export type WithParsedEventFields<T> = T & {
    eventKind: EventKind;
    eventDetails: Event;
  };

  /**
   * Takes an object with `eventKind` and `eventDetails`, parses the details as
   * `Event`, validates the kind matches, and returns a new object with
   * strongly-typed fields. Throws `BadDataError` if parsing or validation
   * fails.
   */
  export function withParsedEventFields<
    T extends { eventKind: string; eventDetails: object },
  >(obj: T): WithParsedEventFields<T> {
    const parseResult = parseEvent(obj.eventDetails);
    if (!parseResult.success) {
      throw new BadDataError('Invalid event', {
        cause: parseResult.error,
        data: { parseResult, input: obj },
      });
    }

    if (parseResult.data.eventKind !== obj.eventKind) {
      throw new BadDataError('Event kind mismatch', {
        cause: parseResult.error,
        data: { parseResult, inputEventKind: obj.eventKind },
      });
    }

    return {
      ...obj,
      eventKind: parseResult.data.eventKind,
      eventDetails: parseResult.data,
    } as WithParsedEventFields<T>;
  }
}
